import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay, useDroppable } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, GripVertical, Save, Trash2, Plus, Layers, List, ChevronDown, ChevronUp, Settings2, FolderPlus, Zap, Calendar, ExternalLink, ListPlus, Pencil, Sparkles } from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';

// Check if field is a system field
const isSystemField = (fieldName) => {
  return fieldName?.startsWith('system_');
};

// Sortable Field Component
const SortableField = ({ field, onUpdate, onDelete, sections, isDragging, optionLists, allFields, caseContextFields, isCaseSpecific }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: field.field_id });
  const [isEditing, setIsEditing] = useState(false);
  const [localField, setLocalField] = useState(field);
  const [newOption, setNewOption] = useState('');
  const [showCustomMask, setShowCustomMask] = useState(false);
  const [showQuickFill, setShowQuickFill] = useState(false);
  const [showConditionalLogic, setShowConditionalLogic] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  // Check if this is a system field (auto-populated)
  const isSystem = isSystemField(field.field_name);

  // Sync localField when field prop changes
  useEffect(() => {
    setLocalField(field);
  }, [field]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'email', label: 'Email' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'tel', label: 'Phone' },
    { value: 'textarea', label: 'Long Text' },
    { value: 'select', label: 'Dropdown' },
    { value: 'radio', label: 'Radio Buttons' },
    { value: 'checkbox', label: 'Checkbox' }
  ];

  const maskPresets = [
    { value: '', label: 'No Mask' },
    { value: '(###) ###-####', label: 'US Phone: (###) ###-####' },
    { value: '##/##/####', label: 'Date: ##/##/####' },
    { value: '###-##-####', label: 'SSN: ###-##-####' },
    { value: '####-####-####-####', label: 'Credit Card: ####-####-####-####' },
    { value: '#####', label: 'ZIP Code: #####' },
    { value: '#####-####', label: 'ZIP+4: #####-####' },
    { value: '@@-####', label: 'Custom: @@-####' },
    { value: 'custom', label: 'Custom Pattern...' }
  ];

  const needsOptions = ['select', 'radio', 'checkbox'].includes(localField.field_type);
  const canHaveMask = ['text', 'tel'].includes(localField.field_type);

  const handleAddOption = () => {
    if (newOption.trim()) {
      const options = localField.options || [];
      const updated = { ...localField, options: [...options, newOption.trim()] };
      setLocalField(updated);
      onUpdate(updated);
      setNewOption('');
    }
  };

  const handleRemoveOption = (index) => {
    const options = [...(localField.options || [])];
    options.splice(index, 1);
    const updated = { ...localField, options };
    setLocalField(updated);
    onUpdate(updated);
  };

  const handleSetDefaultOption = (option) => {
    const updated = { ...localField, default_value: option };
    setLocalField(updated);
    onUpdate(updated);
  };

  // Quick Fill - apply predefined option list
  const handleQuickFill = (list) => {
    const updated = { ...localField, options: [...list.options] };
    setLocalField(updated);
    onUpdate(updated);
    setShowQuickFill(false);
    toast.success(`Applied "${list.name}" options`);
  };

  // Get all available option lists (system + user)
  const allOptionLists = optionLists ? [...(optionLists.system_lists || []), ...(optionLists.user_lists || [])] : [];

  return (
    <div ref={setNodeRef} style={style} className={`p-4 rounded-xl bg-white border transition-colors ${isSystem ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200 hover:border-violet-300'}`} data-testid={`field-${field.field_id}`}>
      <div className="flex items-start gap-3">
        <button
          {...attributes}
          {...listeners}
          className="mt-1 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600"
          data-testid={`drag-handle-${field.field_id}`}
        >
          <GripVertical className="w-4 h-4" />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${isSystem ? 'bg-amber-100 text-amber-700' : 'bg-violet-100 text-violet-700'}`}>
                {field.field_name}
              </span>
              <span className="text-sm text-slate-600">{field.label}</span>
              {isSystem && (
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium flex items-center gap-1" title="This field will be auto-populated at export time">
                  <Calendar className="w-3 h-3" />
                  Auto
                </span>
              )}
              {field.required && <span className="text-red-500 text-xs">*</span>}
              {field.read_only && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-xs">Read-only</span>}
              {field.hidden && <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-600 text-xs">Hidden</span>}
            </div>
            <div className="flex items-center gap-1">
              {!isSystem && (
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-violet-600 transition-colors"
                data-testid={`edit-field-${field.field_id}`}
              >
                <Settings2 className="w-4 h-4" />
              </button>
              )}
              {!isSystem && (
                <button
                  onClick={() => onDelete(field.field_id)}
                  className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                  data-testid={`delete-field-${field.field_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* System Field Info */}
          {isSystem && (
            <div className="mt-2 p-3 rounded-lg bg-amber-50 border border-amber-100">
              <p className="text-sm text-amber-800">
                <strong>Auto-populated field:</strong> This field will be automatically filled with the current date when you export the document. No user input required.
              </p>
            </div>
          )}

          {isEditing && !isSystem && (
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500 mb-1">Label</Label>
                  <Input
                    value={localField.label}
                    onChange={(e) => setLocalField({ ...localField, label: e.target.value })}
                    onBlur={() => onUpdate(localField)}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1">Field Type</Label>
                  <select
                    value={localField.field_type}
                    onChange={(e) => {
                      const updated = { ...localField, field_type: e.target.value };
                      if (!['select', 'radio', 'checkbox'].includes(e.target.value)) {
                        updated.options = null;
                      } else if (!updated.options) {
                        updated.options = [];
                      }
                      setLocalField(updated);
                      onUpdate(updated);
                    }}
                    className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm"
                  >
                    {fieldTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Section + Help Text row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {sections && sections.length > 1 && (
                  <div>
                    <Label className="text-xs text-slate-500 mb-1">Section</Label>
                    <select
                      value={localField.section_id || ''}
                      onChange={(e) => {
                        const updated = { ...localField, section_id: e.target.value };
                        setLocalField(updated);
                        onUpdate(updated);
                      }}
                      className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm"
                    >
                      {sections.map(section => (
                        <option key={section.section_id} value={section.section_id}>{section.title}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className={sections && sections.length > 1 ? '' : 'sm:col-span-2'}>
                  <Label className="text-xs text-slate-500 mb-1">Help Text</Label>
                  <Input
                    value={localField.description || ''}
                    onChange={(e) => setLocalField({ ...localField, description: e.target.value })}
                    onBlur={() => onUpdate(localField)}
                    placeholder="Brief instructions..."
                    className="h-9"
                  />
                </div>
              </div>

              {/* Case Field Mapping - only show for case-specific templates */}
              {isCaseSpecific && caseContextFields && caseContextFields.length > 0 && (
                <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200">
                  <Label className="text-xs font-medium text-indigo-700 mb-2 block">
                    Case Field Mapping (Auto-fill from CRM)
                  </Label>
                  <select
                    value={localField.case_field_mapping || ''}
                    onChange={(e) => {
                      const updated = { ...localField, case_field_mapping: e.target.value || null };
                      setLocalField(updated);
                      onUpdate(updated);
                    }}
                    className="w-full h-9 rounded-md border border-indigo-200 px-3 text-sm bg-white focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
                    data-testid={`case-mapping-${localField.field_id}`}
                  >
                    <option value="">-- No mapping (manual entry) --</option>
                    {/* Group by category */}
                    {['Case', 'Client', 'Attorney', 'Firm', 'Opposing Counsel'].map(category => {
                      const categoryFields = caseContextFields.filter(f => f.category === category);
                      if (categoryFields.length === 0) return null;
                      return (
                        <optgroup key={category} label={category}>
                          {categoryFields.map(cf => (
                            <option key={cf.field_path} value={cf.field_path}>{cf.label}</option>
                          ))}
                        </optgroup>
                      );
                    })}
                  </select>
                  {localField.case_field_mapping && (
                    <p className="text-xs text-indigo-600 mt-1.5">
                      This field will auto-fill with <strong>{localField.case_field_mapping}</strong> when running from a case
                    </p>
                  )}
                </div>
              )}

              {/* Placeholder + Default Value row */}
              {!needsOptions && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1">Placeholder</Label>
                    <Input
                      value={localField.placeholder}
                      onChange={(e) => setLocalField({ ...localField, placeholder: e.target.value })}
                      onBlur={() => onUpdate(localField)}
                      placeholder="Input placeholder..."
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1">Default Value</Label>
                    <Input
                      value={localField.default_value || ''}
                      onChange={(e) => setLocalField({ ...localField, default_value: e.target.value })}
                      onBlur={() => onUpdate(localField)}
                      placeholder="Pre-filled value..."
                      className="h-9"
                    />
                  </div>
                </div>
              )}

              {needsOptions && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs text-slate-500">Options</Label>
                    {/* Quick Fill Button */}
                    <div className="relative">
                      <button
                        onClick={() => setShowQuickFill(!showQuickFill)}
                        className="flex items-center gap-1 px-2 py-1 rounded-md bg-violet-50 text-violet-600 text-xs font-medium hover:bg-violet-100 transition-colors"
                        title="Quick fill from predefined lists"
                      >
                        <Zap className="w-3 h-3" />
                        Quick Fill
                      </button>
                      
                      {/* Quick Fill Dropdown */}
                      {showQuickFill && (
                        <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-20">
                          <div className="p-2 border-b border-slate-100">
                            <p className="text-xs text-slate-500">Select a predefined list to populate options</p>
                          </div>
                          <div className="max-h-48 overflow-y-auto p-1">
                            {allOptionLists.length === 0 ? (
                              <p className="p-2 text-xs text-slate-400 text-center">No option lists available</p>
                            ) : (
                              allOptionLists.map((list) => (
                                <button
                                  key={list.list_id}
                                  onClick={() => handleQuickFill(list)}
                                  className="w-full text-left p-2 rounded-md hover:bg-violet-50 transition-colors"
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-slate-700">{list.name}</span>
                                    {list.is_system && (
                                      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-xs">System</span>
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-400 truncate">{list.options.slice(0, 3).join(', ')}{list.options.length > 3 ? '...' : ''}</p>
                                </button>
                              ))
                            )}
                          </div>
                          <div className="p-2 border-t border-slate-100">
                            <Link
                              to="/global-fields"
                              className="flex items-center justify-center gap-1 text-xs text-violet-600 hover:text-violet-700"
                            >
                              <ListPlus className="w-3 h-3" />
                              Manage Option Lists
                              <ExternalLink className="w-3 h-3" />
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Manual option entry */}
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOption())}
                      placeholder="Add option manually..."
                      className="h-9 flex-1"
                    />
                    <Button type="button" onClick={handleAddOption} size="sm" className="h-9 px-3 bg-violet-600 hover:bg-violet-700">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Option list selector */}
                  {allOptionLists.length > 0 && (
                    <div className="mb-3">
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            const list = allOptionLists.find(l => l.list_id === e.target.value);
                            if (list) handleQuickFill(list);
                            e.target.value = '';
                          }
                        }}
                        className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm text-slate-500"
                        defaultValue=""
                      >
                        <option value="">Or select a predefined list...</option>
                        {allOptionLists.map(list => (
                          <option key={list.list_id} value={list.list_id}>
                            {list.name} ({list.options.length} options)
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Current options */}
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {(localField.options || []).length === 0 ? (
                      <p className="text-xs text-slate-400 text-center py-2">No options yet. Add manually or use Quick Fill.</p>
                    ) : (
                      (localField.options || []).map((option, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 text-sm">
                          <span>{option}</span>
                          <div className="flex items-center gap-1">
                            {localField.default_value === option && (
                              <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-xs">Default</span>
                            )}
                            <button onClick={() => handleSetDefaultOption(option)} className="px-2 py-0.5 rounded text-xs hover:bg-slate-200">Set Default</button>
                            <button onClick={() => handleRemoveOption(idx)} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-6 pt-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={localField.required}
                    onCheckedChange={(checked) => {
                      if (checked && localField.read_only && !localField.default_value) {
                        toast.error('Cannot make read-only field required without a default value');
                        return;
                      }
                      const updated = { ...localField, required: checked };
                      setLocalField(updated);
                      onUpdate(updated);
                    }}
                  />
                  <Label className="text-sm text-slate-600">Required</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={localField.read_only}
                    onCheckedChange={(checked) => {
                      if (checked && localField.required && !localField.default_value) {
                        toast.error('Cannot make required field read-only without a default value');
                        return;
                      }
                      const updated = { ...localField, read_only: checked };
                      setLocalField(updated);
                      onUpdate(updated);
                    }}
                  />
                  <Label className="text-sm text-slate-600">Read-only</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!localField.hidden}
                    onCheckedChange={(checked) => {
                      const updated = { ...localField, hidden: !checked };
                      setLocalField(updated);
                      onUpdate(updated);
                    }}
                  />
                  <Label className="text-sm text-slate-600" title="Show or hide this field in the form">
                    Visible
                  </Label>
                </div>
                {(localField.field_type === 'text' || localField.field_type === 'textarea') && (
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={localField.trim_value !== false}
                      onCheckedChange={(checked) => {
                        const updated = { ...localField, trim_value: checked };
                        setLocalField(updated);
                        onUpdate(updated);
                      }}
                    />
                    <Label className="text-sm text-slate-600" title="Remove leading/trailing spaces from input">
                      Trim spaces
                    </Label>
                  </div>
                )}
              </div>

              {/* Advanced Settings Section - only for text/textarea fields */}
              {(localField.field_type === 'text' || localField.field_type === 'textarea') && (
                <div className="pt-4 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                    className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-violet-600 transition-colors"
                  >
                    <Settings2 className="w-4 h-4" />
                    Advanced Settings
                    {(localField.input_mask || localField.min_length || localField.max_length || localField.output_case) && (
                      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-xs">Configured</span>
                    )}
                    <ChevronDown className={`w-4 h-4 transition-transform ${showAdvancedSettings ? 'rotate-180' : ''}`} />
                  </button>

                  {showAdvancedSettings && (
                    <div className="mt-3 p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-4">
                      {/* Character Limits */}
                      <div>
                        <Label className="text-xs font-medium text-slate-600 mb-2 block">Character Limits</Label>
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <Label className="text-xs text-slate-500 mb-1 block">Min</Label>
                            <Input
                              type="number"
                              min="0"
                              value={localField.min_length || ''}
                              onChange={(e) => {
                                const updated = { ...localField, min_length: e.target.value ? parseInt(e.target.value) : null };
                                setLocalField(updated);
                              }}
                              onBlur={() => onUpdate(localField)}
                              placeholder="No min"
                              className="h-9"
                            />
                          </div>
                          <div className="flex-1">
                            <Label className="text-xs text-slate-500 mb-1 block">Max</Label>
                            <Input
                              type="number"
                              min="0"
                              value={localField.max_length || ''}
                              onChange={(e) => {
                                const updated = { ...localField, max_length: e.target.value ? parseInt(e.target.value) : null };
                                setLocalField(updated);
                              }}
                              onBlur={() => onUpdate(localField)}
                              placeholder="No max"
                              className="h-9"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Output Case */}
                      <div>
                        <Label className="text-xs font-medium text-slate-600 mb-2 block">Output Case (applied on export)</Label>
                        <select
                          value={localField.output_case || ''}
                          onChange={(e) => {
                            const updated = { ...localField, output_case: e.target.value || null };
                            setLocalField(updated);
                            onUpdate(updated);
                          }}
                          className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                        >
                          <option value="">No transformation</option>
                          <option value="uppercase">UPPERCASE</option>
                          <option value="lowercase">lowercase</option>
                          <option value="titlecase">Title Case</option>
                          <option value="sentencecase">Sentence case</option>
                        </select>
                        <p className="text-xs text-slate-400 mt-1">Text will be transformed when exporting the document</p>
                      </div>

                      {/* Input Mask */}
                      {canHaveMask && (
                        <div>
                          <Label className="text-xs font-medium text-slate-600 mb-2 block">Input Mask</Label>
                          <select
                            value={localField.input_mask === null || localField.input_mask === '' ? '' : (maskPresets.find(p => p.value === localField.input_mask) ? localField.input_mask : 'custom')}
                            onChange={(e) => {
                              if (e.target.value === 'custom') {
                                setShowCustomMask(true);
                              } else {
                                setShowCustomMask(false);
                                const updated = { ...localField, input_mask: e.target.value || null };
                                setLocalField(updated);
                                onUpdate(updated);
                              }
                            }}
                            className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                          >
                            {maskPresets.map(preset => (
                              <option key={preset.value} value={preset.value}>{preset.label}</option>
                            ))}
                          </select>
                          
                          {(showCustomMask || (localField.input_mask && !maskPresets.find(p => p.value === localField.input_mask))) && (
                            <div className="mt-2">
                              <Input
                                value={localField.input_mask || ''}
                                onChange={(e) => setLocalField({ ...localField, input_mask: e.target.value })}
                                onBlur={() => onUpdate(localField)}
                                placeholder="e.g., ###-@@-****"
                                className="h-9"
                              />
                              <p className="text-xs text-slate-400 mt-1"># = digit, @ = letter, * = alphanumeric</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Conditional Logic Section */}
              <div className="pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowConditionalLogic(!showConditionalLogic)}
                  className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-violet-600 transition-colors"
                >
                  <Zap className="w-4 h-4" />
                  Conditional Logic
                  {localField.conditional_logic?.enabled && (
                    <span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-xs">Active</span>
                  )}
                  <ChevronDown className={`w-4 h-4 transition-transform ${showConditionalLogic ? 'rotate-180' : ''}`} />
                </button>

                {showConditionalLogic && (
                  <div className="mt-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <div className="flex items-center gap-3 mb-4">
                      <Switch
                        checked={localField.conditional_logic?.enabled || false}
                        onCheckedChange={(checked) => {
                          const updated = {
                            ...localField,
                            conditional_logic: {
                              ...localField.conditional_logic,
                              enabled: checked,
                              action: localField.conditional_logic?.action || 'show',
                              conditions: localField.conditional_logic?.conditions || [],
                              logic: localField.conditional_logic?.logic || 'and'
                            }
                          };
                          setLocalField(updated);
                          onUpdate(updated);
                        }}
                      />
                      <Label className="text-sm text-slate-600">Enable conditional logic</Label>
                    </div>

                    {localField.conditional_logic?.enabled && (
                      <div className="space-y-4">
                        {/* Action Selection */}
                        <div className="flex items-center gap-3">
                          <select
                            value={localField.conditional_logic?.action || 'show'}
                            onChange={(e) => {
                              const updated = {
                                ...localField,
                                conditional_logic: { ...localField.conditional_logic, action: e.target.value }
                              };
                              setLocalField(updated);
                              onUpdate(updated);
                            }}
                            className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white"
                          >
                            <option value="show">Show this field</option>
                            <option value="hide">Hide this field</option>
                          </select>
                          <span className="text-sm text-slate-600">when</span>
                          <select
                            value={localField.conditional_logic?.logic || 'and'}
                            onChange={(e) => {
                              const updated = {
                                ...localField,
                                conditional_logic: { ...localField.conditional_logic, logic: e.target.value }
                              };
                              setLocalField(updated);
                              onUpdate(updated);
                            }}
                            className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white"
                          >
                            <option value="and">ALL conditions match</option>
                            <option value="or">ANY condition matches</option>
                          </select>
                        </div>

                        {/* Conditions */}
                        <div className="space-y-2">
                          {(localField.conditional_logic?.conditions || []).map((condition, idx) => {
                            const sourceField = allFields?.find(f => f.field_id === condition.field_id);
                            const sourceOptions = sourceField?.options || [];
                            
                            return (
                              <div key={idx} className="flex items-center gap-2 p-3 rounded-lg bg-white border border-slate-200">
                                <select
                                  value={condition.field_id || ''}
                                  onChange={(e) => {
                                    const newConditions = [...(localField.conditional_logic?.conditions || [])];
                                    newConditions[idx] = { ...condition, field_id: e.target.value, value: '' };
                                    const updated = {
                                      ...localField,
                                      conditional_logic: { ...localField.conditional_logic, conditions: newConditions }
                                    };
                                    setLocalField(updated);
                                    onUpdate(updated);
                                  }}
                                  className="h-8 px-2 rounded-lg border border-slate-200 text-sm bg-white flex-1"
                                >
                                  <option value="">Select field...</option>
                                  {allFields?.filter(f => f.field_id !== field.field_id && !f.field_name?.startsWith('system_')).map(f => (
                                    <option key={f.field_id} value={f.field_id}>{f.label}</option>
                                  ))}
                                </select>

                                <select
                                  value={condition.operator || 'equals'}
                                  onChange={(e) => {
                                    const newConditions = [...(localField.conditional_logic?.conditions || [])];
                                    newConditions[idx] = { ...condition, operator: e.target.value };
                                    const updated = {
                                      ...localField,
                                      conditional_logic: { ...localField.conditional_logic, conditions: newConditions }
                                    };
                                    setLocalField(updated);
                                    onUpdate(updated);
                                  }}
                                  className="h-8 px-2 rounded-lg border border-slate-200 text-sm bg-white"
                                >
                                  <option value="equals">equals</option>
                                  <option value="not_equals">not equals</option>
                                  <option value="contains">contains</option>
                                  <option value="not_contains">not contains</option>
                                  <option value="is_empty">is empty</option>
                                  <option value="is_not_empty">is not empty</option>
                                  <option value="greater_than">greater than</option>
                                  <option value="less_than">less than</option>
                                </select>

                                {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
                                  sourceOptions.length > 0 ? (
                                    <select
                                      value={condition.value || ''}
                                      onChange={(e) => {
                                        const newConditions = [...(localField.conditional_logic?.conditions || [])];
                                        newConditions[idx] = { ...condition, value: e.target.value };
                                        const updated = {
                                          ...localField,
                                          conditional_logic: { ...localField.conditional_logic, conditions: newConditions }
                                        };
                                        setLocalField(updated);
                                        onUpdate(updated);
                                      }}
                                      className="h-8 px-2 rounded-lg border border-slate-200 text-sm bg-white flex-1"
                                    >
                                      <option value="">Select value...</option>
                                      {sourceOptions.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <Input
                                      value={condition.value || ''}
                                      onChange={(e) => {
                                        const newConditions = [...(localField.conditional_logic?.conditions || [])];
                                        newConditions[idx] = { ...condition, value: e.target.value };
                                        const updated = {
                                          ...localField,
                                          conditional_logic: { ...localField.conditional_logic, conditions: newConditions }
                                        };
                                        setLocalField(updated);
                                        onUpdate(updated);
                                      }}
                                      placeholder="Value"
                                      className="h-8 flex-1"
                                    />
                                  )
                                )}

                                <button
                                  type="button"
                                  onClick={() => {
                                    const newConditions = (localField.conditional_logic?.conditions || []).filter((_, i) => i !== idx);
                                    const updated = {
                                      ...localField,
                                      conditional_logic: { ...localField.conditional_logic, conditions: newConditions }
                                    };
                                    setLocalField(updated);
                                    onUpdate(updated);
                                  }}
                                  className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>

                        {/* Add Condition Button */}
                        <button
                          type="button"
                          onClick={() => {
                            const newConditions = [
                              ...(localField.conditional_logic?.conditions || []),
                              { field_id: '', operator: 'equals', value: '' }
                            ];
                            const updated = {
                              ...localField,
                              conditional_logic: { ...localField.conditional_logic, conditions: newConditions }
                            };
                            setLocalField(updated);
                            onUpdate(updated);
                          }}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-slate-300 text-sm text-slate-600 hover:border-violet-400 hover:text-violet-600 transition-colors w-full justify-center"
                        >
                          <Plus className="w-4 h-4" />
                          Add Condition
                        </button>

                        {(localField.conditional_logic?.conditions || []).length === 0 && (
                          <p className="text-xs text-slate-500 text-center">
                            Add conditions to control when this field appears
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Field preview for drag overlay
const FieldDragPreview = ({ field }) => {
  return (
    <div className="p-4 rounded-xl bg-white border-2 border-violet-400 shadow-lg opacity-90">
      <div className="flex items-center gap-3">
        <GripVertical className="w-4 h-4 text-violet-500" />
        <span className="px-2 py-0.5 rounded-md bg-violet-100 text-violet-700 text-xs font-semibold">
          {field.field_name}
        </span>
        <span className="text-sm text-slate-600">{field.label}</span>
      </div>
    </div>
  );
};

// Droppable Section Component
const DroppableSection = ({ section, fields, onUpdateSection, onDeleteSection, onUpdateField, onDeleteField, canDelete, activeId, allSections, optionLists, allFields, caseContextFields, isCaseSpecific }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [localSection, setLocalSection] = useState(section);

  const { setNodeRef, isOver } = useDroppable({
    id: `section-${section.section_id}`,
    data: { type: 'section', sectionId: section.section_id }
  });

  const sectionFields = fields.filter(f => f.section_id === section.section_id).sort((a, b) => a.order - b.order);

  return (
    <div 
      className={`rounded-2xl border-2 bg-white overflow-hidden transition-colors ${
        isOver ? 'border-violet-400 bg-violet-50' : 'border-slate-200'
      }`} 
      data-testid={`section-${section.section_id}`}
    >
      {/* Section Header */}
      <div className="px-5 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setIsExpanded(!isExpanded)} className="p-1 rounded hover:bg-slate-100">
              {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>
            {isEditing ? (
              <Input
                value={localSection.title}
                onChange={(e) => setLocalSection({ ...localSection, title: e.target.value })}
                onBlur={() => { onUpdateSection(localSection); setIsEditing(false); }}
                onKeyPress={(e) => e.key === 'Enter' && (onUpdateSection(localSection), setIsEditing(false))}
                className="h-8 w-48 text-sm font-semibold"
                autoFocus
              />
            ) : (
              <h3 className="font-semibold text-slate-800">{section.title}</h3>
            )}
            <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-medium">
              {sectionFields.length} field{sectionFields.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
              data-testid={`edit-section-${section.section_id}`}
            >
              <Settings2 className="w-4 h-4" />
            </button>
            {canDelete && (
              <button
                onClick={() => onDeleteSection(section.section_id)}
                className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                data-testid={`delete-section-${section.section_id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        {isEditing && (
          <div className="mt-3">
            <Label className="text-xs text-slate-500 mb-1">Section Description</Label>
            <Input
              value={localSection.description || ''}
              onChange={(e) => setLocalSection({ ...localSection, description: e.target.value })}
              onBlur={() => onUpdateSection(localSection)}
              placeholder="Optional description for this section..."
              className="h-8 text-sm"
            />
          </div>
        )}
        {section.description && !isEditing && (
          <p className="mt-1 ml-8 text-sm text-slate-500">{section.description}</p>
        )}
      </div>

      {/* Section Fields - Droppable Area */}
      {isExpanded && (
        <div ref={setNodeRef} className={`p-4 min-h-[80px] transition-colors ${isOver ? 'bg-violet-50' : ''}`}>
          {sectionFields.length === 0 ? (
            <div className={`py-8 text-center text-sm border-2 border-dashed rounded-xl transition-colors ${
              isOver ? 'border-violet-400 text-violet-600 bg-violet-100' : 'border-slate-200 text-slate-400'
            }`}>
              {isOver ? 'Drop field here' : 'Drag fields here to add them to this section'}
            </div>
          ) : (
            <SortableContext items={sectionFields.map(f => f.field_id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {sectionFields.map(field => (
                  <SortableField
                    key={field.field_id}
                    field={field}
                    onUpdate={onUpdateField}
                    onDelete={onDeleteField}
                    sections={allSections}
                    isDragging={activeId === field.field_id}
                    optionLists={optionLists}
                    allFields={allFields}
                    caseContextFields={caseContextFields}
                    isCaseSpecific={isCaseSpecific}
                  />
                ))}
              </div>
            </SortableContext>
          )}
        </div>
      )}
    </div>
  );
};

// Main Template Editor Component
const TemplateEditor = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('sections');
  const [activeId, setActiveId] = useState(null);
  const [optionLists, setOptionLists] = useState(null);
  const [categories, setCategories] = useState([]);
  const [caseContextFields, setCaseContextFields] = useState([]);
  const [userAppMode, setUserAppMode] = useState('standard');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchTemplate = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/templates/${templateId}`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load template');
      const data = await response.json();
      
      if (!data.sections || data.sections.length === 0) {
        const defaultSectionId = `sec_${Date.now().toString(36)}`;
        data.sections = [{ section_id: defaultSectionId, title: 'General Information', description: null, order: 0 }];
        data.fields = data.fields.map(f => ({ ...f, section_id: f.section_id || defaultSectionId }));
      }
      if (!data.display_mode) {
        data.display_mode = 'long_form';
      }
      
      setTemplate(data);
    } catch (error) {
      toast.error(error.message);
      navigate('/templates');
    } finally {
      setLoading(false);
    }
  }, [templateId, navigate]);

  const fetchOptionLists = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/global-fields/option-lists`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOptionLists(data);
      }
    } catch (error) {
      console.error('Failed to fetch option lists:', error);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/categories`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  const fetchUserAndCaseContextFields = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get user info to check app_mode
      const userRes = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (userRes.ok) {
        const userData = await userRes.json();
        setUserAppMode(userData.app_mode || 'standard');
        
        // Only fetch case context fields if user is in CRM mode
        if (userData.app_mode === 'crm') {
          const ctxRes = await fetch(`${API}/templates/case-context-fields`, {
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (ctxRes.ok) {
            const ctxData = await ctxRes.json();
            setCaseContextFields(ctxData.fields || []);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch user/case context fields:', error);
    }
  }, []);

  useEffect(() => {
    fetchTemplate();
    fetchOptionLists();
    fetchCategories();
    fetchUserAndCaseContextFields();
  }, [fetchTemplate, fetchOptionLists, fetchCategories, fetchUserAndCaseContextFields]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeField = template.fields.find(f => f.field_id === active.id);
    if (!activeField) return;

    // Check if dropped on a section drop zone
    if (over.id.toString().startsWith('section-')) {
      const targetSectionId = over.id.toString().replace('section-', '');
      
      // Move field to new section
      if (activeField.section_id !== targetSectionId) {
        setTemplate(prev => ({
          ...prev,
          fields: prev.fields.map(f => 
            f.field_id === active.id 
              ? { ...f, section_id: targetSectionId } 
              : f
          )
        }));
        toast.success('Field moved to new section');
      }
      return;
    }

    // Check if dropped on another field (reordering within same section)
    const overField = template.fields.find(f => f.field_id === over.id);
    if (overField) {
      // If different sections, move to target section and reorder
      if (activeField.section_id !== overField.section_id) {
        setTemplate(prev => {
          const updatedFields = prev.fields.map(f => 
            f.field_id === active.id 
              ? { ...f, section_id: overField.section_id } 
              : f
          );
          
          // Now reorder within the new section
          const sectionFields = updatedFields.filter(f => f.section_id === overField.section_id);
          const oldIndex = sectionFields.findIndex(f => f.field_id === active.id);
          const newIndex = sectionFields.findIndex(f => f.field_id === over.id);
          
          const reorderedSection = arrayMove(sectionFields, oldIndex, newIndex);
          reorderedSection.forEach((f, idx) => { f.order = idx; });
          
          const otherFields = updatedFields.filter(f => f.section_id !== overField.section_id);
          return { ...prev, fields: [...otherFields, ...reorderedSection] };
        });
        toast.success('Field moved to new section');
      } else {
        // Same section, just reorder
        setTemplate(prev => {
          const sectionFields = prev.fields.filter(f => f.section_id === activeField.section_id);
          const otherFields = prev.fields.filter(f => f.section_id !== activeField.section_id);
          
          const oldIndex = sectionFields.findIndex(f => f.field_id === active.id);
          const newIndex = sectionFields.findIndex(f => f.field_id === over.id);
          
          const reorderedSection = arrayMove(sectionFields, oldIndex, newIndex);
          reorderedSection.forEach((f, idx) => { f.order = idx; });
          
          return { ...prev, fields: [...otherFields, ...reorderedSection] };
        });
      }
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    // Preview section change during drag
    if (over.id.toString().startsWith('section-')) {
      // Could add visual preview here
    }
  };

  const handleFieldUpdate = (updatedField) => {
    setTemplate(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.field_id === updatedField.field_id ? updatedField : f)
    }));
  };

  const handleFieldDelete = (fieldId) => {
    setTemplate(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.field_id !== fieldId)
    }));
  };

  const handleAddSection = () => {
    const newSection = {
      section_id: `sec_${Date.now().toString(36)}`,
      title: `Section ${(template.sections?.length || 0) + 1}`,
      description: null,
      order: template.sections?.length || 0
    };
    setTemplate(prev => ({
      ...prev,
      sections: [...(prev.sections || []), newSection]
    }));
    toast.success('Section added');
  };

  const handleUpdateSection = (updatedSection) => {
    setTemplate(prev => ({
      ...prev,
      sections: prev.sections.map(s => s.section_id === updatedSection.section_id ? updatedSection : s)
    }));
  };

  const handleDeleteSection = (sectionId) => {
    if (template.sections.length <= 1) {
      toast.error('Cannot delete the only section');
      return;
    }
    const firstSectionId = template.sections.find(s => s.section_id !== sectionId)?.section_id;
    setTemplate(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.section_id !== sectionId),
      fields: prev.fields.map(f => f.section_id === sectionId ? { ...f, section_id: firstSectionId } : f)
    }));
    toast.success('Section deleted. Fields moved to another section.');
  };

  const handleDisplayModeChange = (mode) => {
    setTemplate(prev => ({ ...prev, display_mode: mode }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/templates/${templateId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: template.name,
          description: template.description,
          fields: template.fields,
          sections: template.sections,
          display_mode: template.display_mode,
          is_case_specific: template.is_case_specific,
          is_public: template.is_public,
          category: template.category,
          tags: template.tags
        })
      });
      if (!response.ok) throw new Error('Failed to save template');
      toast.success('Template saved successfully!');
      navigate('/templates');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  const sortedSections = [...(template.sections || [])].sort((a, b) => a.order - b.order);
  const activeField = activeId ? template.fields.find(f => f.field_id === activeId) : null;
  
  // Compute auto-mapped case fields for the banner
  const autoMappedFields = template.fields?.filter(f => f.case_field_mapping) || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <Button variant="ghost" onClick={() => navigate('/templates')} className="rounded-lg" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate(`/templates/${templateId}/run`)}
                variant="outline"
                className="h-9 rounded-lg"
                data-testid="test-run-btn"
              >
                Test Run
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="h-9 px-5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-medium"
                data-testid="save-template-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Auto-mapped Case Fields Banner */}
        {userAppMode === 'crm' && template.is_case_specific && autoMappedFields.length > 0 && (
          <div className="mb-6 p-4 rounded-xl bg-indigo-50 border border-indigo-200" data-testid="auto-mapped-banner">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-indigo-100">
                <Sparkles className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-indigo-900">
                  {autoMappedFields.length} Case-Specific Field{autoMappedFields.length !== 1 ? 's' : ''} Auto-Detected
                </h3>
                <p className="text-sm text-indigo-700 mt-1">
                  The following fields were automatically mapped to CRM data and will be pre-filled when running this template from a case:
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {autoMappedFields.map(field => (
                    <span 
                      key={field.field_id} 
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-100 text-indigo-800 text-sm font-medium"
                    >
                      <code className="text-xs bg-indigo-200 px-1.5 py-0.5 rounded">{`{{${field.case_field_mapping}}}`}</code>
                      <span className="text-indigo-500">→</span>
                      {field.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">{template.name}</h1>
          <p className="text-slate-500 mt-1">Configure form fields and sections • Drag fields between sections to reorganize</p>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
        >
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-3 space-y-6">
              {/* Display Mode & View Toggle */}
              <div className="p-4 rounded-xl bg-white border border-slate-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Form Display Mode</Label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDisplayModeChange('long_form')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          template.display_mode === 'long_form'
                            ? 'bg-violet-100 text-violet-700 border-2 border-violet-300'
                            : 'bg-slate-100 text-slate-600 border-2 border-transparent hover:bg-slate-200'
                        }`}
                        data-testid="long-form-mode-btn"
                      >
                        <List className="w-4 h-4" />
                        Long Form
                      </button>
                      <button
                        onClick={() => handleDisplayModeChange('wizard')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          template.display_mode === 'wizard'
                            ? 'bg-violet-100 text-violet-700 border-2 border-violet-300'
                            : 'bg-slate-100 text-slate-600 border-2 border-transparent hover:bg-slate-200'
                        }`}
                        data-testid="wizard-mode-btn"
                      >
                        <Layers className="w-4 h-4" />
                        Wizard Steps
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      {template.display_mode === 'wizard' 
                        ? 'Users will navigate through sections as numbered steps' 
                        : 'All sections displayed on a single page'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">View:</span>
                    <div className="flex bg-slate-100 rounded-lg p-0.5">
                      <button
                        onClick={() => setViewMode('sections')}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                          viewMode === 'sections' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        By Section
                      </button>
                      <button
                        onClick={() => setViewMode('all')}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                          viewMode === 'all' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        All Fields
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sections View */}
              {viewMode === 'sections' ? (
                <div className="space-y-4">
                  {sortedSections.map((section) => (
                    <DroppableSection
                      key={section.section_id}
                      section={section}
                      fields={template.fields}
                      onUpdateSection={handleUpdateSection}
                      onDeleteSection={handleDeleteSection}
                      onUpdateField={handleFieldUpdate}
                      onDeleteField={handleFieldDelete}
                      canDelete={template.sections.length > 1}
                      activeId={activeId}
                      allSections={template.sections}
                      optionLists={optionLists}
                      allFields={template.fields}
                      caseContextFields={caseContextFields}
                      isCaseSpecific={template.is_case_specific}
                    />
                  ))}
                  <button
                    onClick={handleAddSection}
                    className="w-full py-4 rounded-xl border-2 border-dashed border-slate-300 text-slate-500 hover:border-violet-400 hover:text-violet-600 hover:bg-violet-50 transition-colors flex items-center justify-center gap-2"
                    data-testid="add-section-btn"
                  >
                    <FolderPlus className="w-5 h-5" />
                    Add Section
                  </button>
                </div>
              ) : (
                /* All Fields View */
                <div className="p-5 rounded-2xl bg-white border border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold text-slate-800">All Fields ({template.fields.length})</h2>
                  </div>
                  <SortableContext items={template.fields.map(f => f.field_id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-3">
                      {template.fields.sort((a, b) => a.order - b.order).map(field => (
                        <SortableField
                          key={field.field_id}
                          field={field}
                          onUpdate={handleFieldUpdate}
                          onDelete={handleFieldDelete}
                          sections={template.sections}
                          isDragging={activeId === field.field_id}
                          optionLists={optionLists}
                          allFields={template.fields}
                          caseContextFields={caseContextFields}
                          isCaseSpecific={template.is_case_specific}
                        />
                      ))}
                    </div>
                  </SortableContext>
                  {template.fields.length === 0 && (
                    <div className="py-12 text-center text-slate-400">No fields extracted from document</div>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="p-5 rounded-xl bg-white border border-slate-200 sticky top-20 space-y-6">
                {/* Editable Template Details */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Pencil className="w-4 h-4" />
                    Template Details
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Name</Label>
                      <Input
                        value={template.name || ''}
                        onChange={(e) => setTemplate(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Template name"
                        className="h-9 text-sm rounded-lg"
                        data-testid="template-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Description</Label>
                      <Textarea
                        value={template.description || ''}
                        onChange={(e) => setTemplate(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Describe this template..."
                        rows={3}
                        className="text-sm rounded-lg resize-none"
                        data-testid="template-description-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Category</Label>
                      <select
                        value={template.category || ''}
                        onChange={(e) => setTemplate(prev => ({ ...prev, category: e.target.value || null }))}
                        className="w-full h-9 text-sm rounded-lg border border-slate-200 px-3 bg-white focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
                        data-testid="template-category-select"
                      >
                        <option value="">Select a category</option>
                        {categories.map(cat => (
                          <option key={cat.category_id} value={cat.slug}>{cat.name}</option>
                        ))}
                      </select>
                      <Link 
                        to="/categories" 
                        className="text-xs text-violet-600 hover:text-violet-700 mt-1.5 inline-block"
                      >
                        Manage categories →
                      </Link>
                    </div>
                    
                    {/* Case-Specific Template Toggle - Only for CRM users */}
                    {userAppMode === 'crm' && (
                      <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-sm font-medium text-indigo-700">Case-Specific Template</Label>
                            <p className="text-xs text-indigo-500 mt-0.5">Enable to map fields to case data</p>
                          </div>
                          <Switch
                            checked={template.is_case_specific || false}
                            onCheckedChange={(checked) => setTemplate(prev => ({ ...prev, is_case_specific: checked }))}
                            data-testid="case-specific-toggle"
                          />
                        </div>
                        {template.is_case_specific && (
                          <p className="text-xs text-indigo-600 mt-2 pt-2 border-t border-indigo-200">
                            Fields can now be mapped to case data. Open a field&apos;s settings to configure mappings.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Template Stats */}
                <div className="pt-4 border-t">
                  <h4 className="text-xs font-medium text-slate-400 uppercase mb-3">Statistics</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-3 rounded-lg bg-slate-50">
                      <span className="text-slate-500 text-xs">Fields</span>
                      <p className="text-lg font-bold text-slate-900">{template.fields.length}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <span className="text-slate-500 text-xs">Sections</span>
                      <p className="text-lg font-bold text-slate-900">{template.sections?.length || 1}</p>
                    </div>
                  </div>
                  <div className="mt-3 p-3 rounded-lg bg-slate-50 text-sm">
                    <span className="text-slate-500 text-xs">File Size</span>
                    <p className="font-medium text-slate-900">{(template.file_size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
                
                {/* Quick Links */}
                <div className="pt-4 border-t">
                  <h4 className="text-xs font-medium text-slate-400 uppercase mb-3">Quick Links</h4>
                  <Link
                    to="/global-fields"
                    className="flex items-center gap-2 p-3 rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors"
                  >
                    <Calendar className="w-4 h-4" />
                    <div>
                      <p className="text-sm font-medium">Global Fields</p>
                      <p className="text-xs text-violet-500">Manage system fields & option lists</p>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeField ? <FieldDragPreview field={activeField} /> : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
};

export default TemplateEditor;
